<script setup>
import AppLayout from '@/Layouts/AppLayout.vue';
import { ref } from 'vue';
import { Link, useForm, router } from '@inertiajs/vue3';

const props = defineProps({
    user: Object,
    year: String,
});

const form = useForm({
    _method: 'PUT',
    name: props.user.name,
    email: props.user.email,
    photo: null,
});

const verificationLinkSent = ref(null);
const photoPreview = ref(null);
const photoInput = ref(null);

const updateProfileInformation = () => {
    if (photoInput.value) {
        form.photo = photoInput.value.files[0];
    }

    form.post(route('user-profile-information.update'), {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onSuccess: () => clearPhotoFileInput(),
    });
};

const sendEmailVerification = () => {
    verificationLinkSent.value = true;
};

const selectNewPhoto = () => {
    photoInput.value.click();
};

const updatePhotoPreview = () => {
    const photo = photoInput.value.files[0];

    if (!photo) return;

    const reader = new FileReader();

    reader.onload = (e) => {
        photoPreview.value = e.target.result;
    };

    reader.readAsDataURL(photo);
};

const deletePhoto = () => {
    router.delete(route('current-user-photo.destroy'), {
        preserveScroll: true,
        onSuccess: () => {
            photoPreview.value = null;
            clearPhotoFileInput();
        },
    });
};

const clearPhotoFileInput = () => {
    if (photoInput.value?.value) {
        photoInput.value.value = null;
    }
};

</script>

<template>
    <AppLayout title="Profile" :year=year :user=user>
        <div class="container-xxl flex-grow-1 container-p-y">
            <h4 class="fw-bold py-3 mb-2">Account Settings</h4>
            <p class="mb-4">Update your account's profile information and email address.</p>

            <div class="row">
                <div class="col-md-12">
                    <ul class="nav nav-pills flex-column flex-md-row mb-3">
                        <li class="nav-item">
                            <Link class="nav-link active" href="/user/profile"><i class="bx bx-user me-1"></i>
                            Account</Link>
                        </li>
                        <li class="nav-item">
                            <Link class="nav-link" href="/users/security"><i class="bx bx-lock-alt me-1"></i>
                            Security</Link>
                        </li>
                    </ul>
                    <div class="card mb-4" v-if="$page.props.jetstream.canUpdateProfileInformation">
                        <h5 class="card-header">Profile Details</h5>

                        <div v-if="$page.props.jetstream.hasEmailVerification && user.email_verified_at === null">
                            <p class="text-sm mt-2">
                                Your email address is unverified.

                                <Link :href="route('verification.send')" method="post" as="button"
                                    class="underline text-gray-600 hover:text-gray-900"
                                    @click.prevent="sendEmailVerification">
                                Click here to re-send the verification email.
                                </Link>
                            </p>

                            <div v-show="verificationLinkSent" class="mt-2 font-medium text-sm text-green-600">
                                A new verification link has been sent to your email address.
                            </div>
                        </div>
                        <form @submit.prevent="updateProfileInformation">
                            <div class="card-body">
                                <div v-if="$page.props.jetstream.managesProfilePhotos"
                                    class="d-flex align-items-start align-items-sm-center gap-4">
                                    <input ref="photoInput" type="file" class="d-none" @change="updatePhotoPreview">
                                    <!-- Current Profile Photo -->
                                    <div v-show="!photoPreview">
                                        <img :src="user.profile_photo_url" :alt="user.name" class="d-block rounded"
                                            height="100" width="100" />
                                    </div>
                                    <!-- New Profile Photo Preview -->
                                    <div v-show="photoPreview">
                                        <img :src="photoPreview" :alt="user.name" class="d-block rounded" height="100"
                                            width="100" />
                                    </div>
                                    <div class="button-wrapper">
                                        <button class="btn btn-warning me-2 mb-4" @click.prevent="selectNewPhoto">
                                            <span class="d-none d-sm-block">Upload new photo</span>
                                            <i class="bx bx-upload d-block d-sm-none"></i>
                                        </button>
                                        <button v-if="user.profile_photo_path" class="btn btn-danger me-2 mb-4"
                                            @click.prevent="deletePhoto">
                                            <span class="d-none d-sm-block">Remove Photo</span>
                                            <i class="bx bx-upload d-block d-sm-none"></i>
                                        </button>
                                        <p class="text-muted mb-0">Allowed jpg, jpeg or png. Dimension 200px X 200px</p>
                                        <div class="text-danger">{{ form.errors.photo }}</div>
                                    </div>
                                </div>
                            </div>
                            <hr class="my-0" />
                            <div class="card-body">
                                <div class="row">
                                    <!-- <p><strong>Agency:</strong> {{ user }}</p> -->
                                    <div class="mb-3 col-md-6">
                                        <label for="firstName" class="form-label">Name</label>
                                        <input class="form-control" type="text" v-model="form.name" placeholder="Full Name"
                                            :class="{ 'is-invalid': form.errors.name }" />
                                        <div v-if="form.errors.name" class="invalid-feedback">
                                            <div v-text="form.errors.name"></div>
                                        </div>
                                    </div>
                                    <div class="mb-3 col-md-6">
                                        <label for="email" class="form-label">E-mail</label>
                                        <input class="form-control" type="email" v-model="form.email"
                                            placeholder="john@domain.com" :class="{ 'is-invalid': form.errors.email }" />
                                        <div v-if="form.errors.email" class="invalid-feedback">
                                            <div v-text="form.errors.email"></div>
                                        </div>
                                    </div>
                                </div>
                                <div class="mt-2">
                                    <button type="submit" class="btn btn-success me-2"
                                        :class="{ 'opacity-25': form.processing }" :disabled="form.processing">Save
                                        changes</button>

                                    <span v-show="form.recentlySuccessful" class="mr-3">
                                        <span leave-active-class="transition ease-in duration-1000"
                                            leave-from-class="opacity-100" leave-to-class="opacity-0"
                                            class="text-secondary">
                                            <span class="text-danger">Changes Saved</span>
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </form>
                    </div>

                </div>
            </div>
        </div>
    </AppLayout>
</template>
